import React, { useState } from "react";
import { Container } from "react-bootstrap";
import img1 from "./near_fiction_1.jpg";
import img2 from "./near_fiction_2.jpg";
import img3 from "./near_fiction_3.jpg";
import img4 from "./near_fiction_4.jpg";
import brochure1 from "./near_fiction_brochure_1.png";
import brochure2 from "./near_fiction_brochure_2.png";

const images = [
  { src: img1, alt: "Near Fiction – installation view 1" },
  { src: img2, alt: "Near Fiction – installation view 2" },
  { src: img3, alt: "Near Fiction – installation view 3" },
  { src: img4, alt: "Near Fiction – installation view 4" },
  { src: brochure1, alt: "Near Fiction – exhibit brochure page 1" },
  { src: brochure2, alt: "Near Fiction – exhibit brochure page 2" },
];

const works = [
  { title: "Walden", year: "2026", medium: "Oil on canvas", size: '30 × 40 in.' },
  { title: "Still Life", year: "2026", medium: "Oil on canvas", size: '16 × 20 in.' },
  { title: "Midsummer", year: "2026", medium: "Oil on canvas", size: '16 × 20 in.' },
  { title: "Blue Moon", year: "2026", medium: "Oil on canvas", size: '16 × 20 in.' },
  { title: "Infinite Jest", year: "2026", medium: "Oil on canvas", size: '11 × 14 in.' },
  { title: "Sunshine on Sick Days", year: "2026", medium: "Oil on canvas", size: '11 × 14 in.' },
  { title: "View of the Green", year: "2026", medium: "Oil on canvas", size: '36 × 48 in.' },
  { title: "Girl in Bed", year: "2026", medium: "Oil on canvas", size: '11 × 14 in.' },
  { title: "Monterey", year: "2025", medium: "Oil on canvas", size: '24 × 32 in.' },
  { title: "Cafe Scene", year: "2025", medium: "Oil on canvas", size: '24 × 40 in.' },
  { title: "T.S. Eliot", year: "2025", medium: "Oil on canvas", size: '12 × 16 in.' },
  { title: "Oranges", year: "2025", medium: "Oil on canvas", size: '12 × 16 in.' },
  { title: "What the Frog\u2019s Eye Tells the Frog\u2019s Brain", year: "2025", medium: "Oil on canvas", size: '12 × 16 in.' },
  { title: "Untitled", year: "2025", medium: "Acrylic on canvas", size: '2 × 2 ft.' },
  { title: "In Translation", year: "2024", medium: "Oil on canvas", size: '1 × 1 ft.' },
];

function NearFiction() {
  const [current, setCurrent] = useState(0);

  const prev = () => setCurrent((c) => (c === 0 ? images.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === images.length - 1 ? 0 : c + 1));

  return (
    <Container className="art-section">
      <div className="exhibit-detail-page">
        <div className="exhibit-detail-header">
          <h1 className="exhibit-detail-title">Near Fiction</h1>
          <p className="exhibit-detail-venue">
            <a href="https://rebeccamolayem.com" target="_blank" rel="noopener noreferrer">
              Rebecca Molayem Gallery
            </a>
          </p>
          <p className="exhibit-detail-info">Los Angeles, CA</p>
          <p className="exhibit-detail-info">July 25, 2026</p>
        </div>

        <div className="exhibit-carousel">
          <button className="carousel-btn carousel-btn-left" onClick={prev} aria-label="Previous image">&#8249;</button>
          <img src={images[current].src} alt={images[current].alt} className="exhibit-detail-img" />
          <button className="carousel-btn carousel-btn-right" onClick={next} aria-label="Next image">&#8250;</button>
        </div>
        <div className="carousel-dots">
          {images.map((_, i) => (
            <span
              key={i}
              className={`carousel-dot ${i === current ? "active" : ""}`}
              onClick={() => setCurrent(i)}
            />
          ))}
        </div>

        {/* Press Release */}
        <div className="exhibit-detail-work">
          <div className="exhibit-detail-about">
            <p><em>'Near Fiction,'</em> is the first solo exhibition by Sky Jung, a California-based painter whose work explores the unstable territory between perception, memory, and the construction of images. The exhibition opens on July 25, 2026, at 481 South Fairfax Avenue, Los Angeles, and features fifteen paintings in oil and acrylic on canvas produced between 2024 and 2026.</p>

            <p>The exhibition takes its title from an ongoing inquiry into the duality of human perception—the way experience registers as viscerally true and fictitious at the same time. In every moment, Jung proposes, there exists a fiction that gives away silent anticipations, longings, and memories of things past: embellishments and nuances that narrate our lives the way we actually perceive them. Through image-making, she explores what she describes as "the inescapable imagination that invites us near fiction to sit in the company of an unpossessable time and place, where truth and subjectivity briefly overlap."</p>

            <p>The exhibition ranges from intimate studies—a 12 × 16-inch portrait of T.S. Eliot, a one-foot square abstraction titled <em>'In Translation'</em>—to the landscape <em>'View of the Green'</em> (2026), a 36 × 48-inch canvas whose loose, atmospheric rendering of California's central valley dissolves the boundary between observation and invention. <em>'Walden'</em> (2026), the exhibition's second largest painting at 30 × 40 inches, takes its title from Thoreau's <em>Walden</em>, extending the exhibition's concern with the private, interior dimensions of experience that turns "real" places into imaginative landscapes.</p>

            <blockquote className="exhibit-detail-quote" style={{ color: '#8C1812' }}>
              "In either case, my experience falls within my own circle, a circle closed on the outside; the whole world for each is peculiar and private to that soul."
              <cite>— T.S. Eliot, <em>The Waste Land</em> (Notes)</cite>
            </blockquote>

            <p>For Jung, <em>'Near Fiction'</em> gives viewers permission to let their private lives tell them what is true.</p>
          </div>
        </div>

        {/* About the Artist */}
        <div className="exhibit-detail-work">
          <h2 className="exhibit-detail-section-title">About the Artist</h2>
          <div className="exhibit-detail-about">
            <p>Sky Jung (b. California) is a painter based in California whose work explores the relationship between perception, memory, and the construction of images. Working primarily in oil and acrylic, she creates paintings that emerge through cycles of accumulation, revision, and removal, revealing forms between recognition and abstraction. Drawing from a background in computer science and engineering, Jung's practice explores the subjective nature of human perception in resistance to technology's influence on how we see the world.</p>

            <p>Jung received her A.B. from Harvard College in 2025, with a concentration in Computer Science and a secondary in Art, Film, and Visual Studies. Her work was included in the Art, Film, & Visual Studies: Open Studios 2025 at the Carpenter Center for the Visual Arts, Cambridge, MA, and the Open Call: Summer Show at Francis Gallery, Los Angeles (2026). She is also the founder of <a href="https://docentofficial.com" target="_blank" rel="noopener noreferrer">Docent</a>, a social museum and gallery guide connecting people with art in 150+ venues across 30 countries.</p>
          </div>
        </div>

        {/* Works on View */}
        <div className="exhibit-detail-work">
          <h2 className="exhibit-detail-section-title">Works on View</h2>
          <div className="exhibit-detail-about">
            {works.map((w, i) => (
              <div key={i} style={{ marginBottom: '1.2rem' }}>
                <p style={{ margin: 0 }}><em>{w.title}</em>, {w.year}</p>
                <p style={{ margin: 0, color: '#8c8a8a', fontSize: '0.9em' }}>{w.medium}</p>
                <p style={{ margin: 0, color: '#8c8a8a', fontSize: '0.9em' }}>{w.size}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div className="exhibit-detail-work" style={{ borderTop: '1px solid #e0e0e0', paddingTop: '2rem', marginTop: '1rem' }}>
          <p style={{ color: '#8c8a8a', fontSize: '0.85em' }}>For additional information, please contact:</p>
          <p style={{ color: '#8c8a8a', fontSize: '0.85em' }}>Sky Jung, <a href="mailto:skyjungofficial@gmail.com">skyjungofficial@gmail.com</a></p>
          <p style={{ color: '#8c8a8a', fontSize: '0.85em' }}><a href="https://skyyjung.com" target="_blank" rel="noopener noreferrer">skyyjung.com</a> | <a href="https://instagram.com/skyyjung" target="_blank" rel="noopener noreferrer">@skyyjung</a></p>
        </div>
      </div>
    </Container>
  );
}

export default NearFiction;
